import {
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { debounceTime, fromEvent, Subject } from 'rxjs';
import { DocumentDownloadService } from '../../services/document-download.service';
import { DownloadFileService } from '../../services/download-file.service';
import { PrintFileService } from '../../services/print-file.service';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

@Component({
    selector: 'apcam-pdfviewer',
    templateUrl: './pdfviewer.component.html',
    styleUrls: ['./pdfviewer.component.scss'],
    imports: [
        LoadingSpinnerComponent,
        MatButtonModule,
        MatIconModule,
        TranslatePipe,
    ],
})
export class PDFViewerComponent implements OnInit, OnChanges {
    private static readonly RESIZE_DEBOUNCE_TIME_MS = 500; // [ms]

    @Input()
    pdfUrl: string = '';

    @Input()
    download: Subject<void> = new Subject();

    @Input()
    fileName: string = '';

    @Input()
    print: Subject<void> = new Subject();

    @Output()
    pdfLoaded: EventEmitter<boolean> = new EventEmitter();

    public pdfDocument: PDFDocumentProxy | null = null;
    public currPage: number = 1;
    public pageCount: number = 0;
    private rendering: boolean = false;
    public timestamp: number = 0;

    public readonly loadFailed = signal(false);

    private pdfjs?: Promise<typeof import('pdfjs-dist')>;

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly downloadFileService: DownloadFileService,
        private readonly downloadService: DocumentDownloadService,
        private readonly printFileService: PrintFileService
    ) {
        this.timestamp = Date.now();
    }

    ngOnChanges(): void {
        this.pdfDocument = null;
        this.getPdfData(this.pdfUrl);
    }

    /**
     * Perf: load `pdfjs-dist` on demand so it stays out of the boot-reachable chunk.
     *
     * The static import hoisted all of pdfjs into the shared vendor chunk that
     * `index.html` modulepreloads, so every visitor paid for it even though the
     * viewer is only reachable through lazy routes and the media-viewer dialog.
     * Measured on the initial payload (the chunks index.html preloads):
     * client 837.5 -> 719.5 kB gzip, admin 850.9 -> 732.8 kB gzip (-118 kB each).
     */
    private loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
        return (this.pdfjs ??= import('pdfjs-dist')
            .then((pdfjs) => {
                pdfjs.GlobalWorkerOptions.workerSrc = 'pdf.worker.min.mjs';
                return pdfjs;
            })
            .catch((err) => {
                this.pdfjs = undefined;
                throw err;
            }));
    }

    ngOnInit(): void {
        this.getPdfData(this.pdfUrl);

        this.download
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.downloadPdf();
            });
        this.print
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async () => {
                this.printPdf();
            });

        fromEvent(window, 'resize')
            .pipe(
                debounceTime(PDFViewerComponent.RESIZE_DEBOUNCE_TIME_MS),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.renderPage(this.currPage);
            });
    }

    private async downloadPdf() {
        const file = await this.prepareDataForDownloadOrPrint();
        if (file) {
            this.downloadFileService.downloadBlobFile(file, this.fileName);
        }
    }

    private async printPdf() {
        const file = await this.prepareDataForDownloadOrPrint();
        if (file) {
            this.printFileService.printBlobFile(file, '.container-pdf-preview');
        }
    }

    private async prepareDataForDownloadOrPrint(): Promise<Blob | null> {
        if (this.pdfDocument) {
            const data = await this.pdfDocument.getData();
            // Ensure we pass an ArrayBuffer-backed view, not SharedArrayBuffer
            const safeData = new Uint8Array(data);
            return new Blob([safeData], { type: 'application/pdf' });
        }
        return null;
    }

    protected retry() {
        this.getPdfData(this.pdfUrl);
    }

    protected getPdfData(url: string) {
        this.loadFailed.set(false);
        this.downloadService.downloadFileToBuffer(url).subscribe({
            next: (data) => {
                let bufferSize = data.byteLength;
                let pdfData = new Uint8Array(data, 0, bufferSize);
                this.processPDFData(pdfData);
            },
            error: () => {
                this.loadFailed.set(true);
            },
        });
    }

    private async processPDFData(data: Uint8Array) {
        try {
            const { getDocument } = await this.loadPdfjs();
            this.pdfDocument = await getDocument({ data: data }).promise;

            this.pageCount = this.pdfDocument.numPages;
            if (this.pageCount > 0) {
                this.currPage = 1;
                this.renderPage(this.currPage);
            }
            // Here, the PDF is fully loaded
            this.pdfLoaded.emit(true);
        } catch {
            // Without this the promise rejects unhandled, `pdfDocument` stays null and
            // the template spins forever.
            this.loadFailed.set(true);
        }
    }

    protected renderPage(pageNum: number) {
        if (this.rendering) return;
        if (this.pdfDocument == null) return;

        this.pdfDocument.getPage(pageNum).then((page) => {
            let canvas = document.getElementById(
                'pdf-preview-canvas-' + this.timestamp.toString()
            ) as HTMLCanvasElement | null;
            if (canvas == null) return;
            let context = canvas.getContext('2d');
            if (context == null) return;
            let pdfWidth = page.getViewport({ scale: 1.0 }).width;
            let pdfHeight = page.getViewport({ scale: 1.0 }).height;
            canvas.width = pdfWidth;
            canvas.height = pdfHeight;
            let scale = 1;
            let viewport = page.getViewport({ scale: scale });
            let renderContext = {
                canvasContext: context,
                canvas: canvas,
                viewport: viewport,
            };
            this.rendering = true;
            page.render(renderContext).promise.then(() => {
                this.rendering = false;
                this.currPage = pageNum;
            });
        });
    }
}
